import { useEffect, useRef, useCallback, useState } from "react";
import { devLog } from "../utils/logger";

/**
 * Hook to trigger a callback when user scrolls near the bottom
 * Uses Intersection Observer API to detect when a sentinel element becomes visible
 *
 * @param {Function} onLoadMore - Callback to load more items
 * @param {boolean} hasMore - Whether there are more items to load
 * @param {boolean} loading - Whether currently loading
 * @param {number} threshold - Distance from bottom to trigger (0.0 to 1.0, default 0.8)
 * @param {RefObject} rootRef - Optional ref to scrollable container (defaults to viewport)
 * @returns {RefObject} - Ref to attach to sentinel element
 */
export function useInfiniteScroll(
  onLoadMore,
  hasMore,
  loading,
  threshold = 0.1,
  rootRef = null,
) {
  /**
   * By having these as ref's, we make sure they persists thriygh re-renders
   * No need to recreate them on re-renders
   */
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  /**
   * Track if elements are ready to prevent infinite loops
   */
  const elementsReadyRef = useRef(false);
  const [elementsMountedCount, setElementsMounted] = useState(0);

  /**
   * Entries is an array of intersectionObserverEntry objects (changes detected by observer)
   */
  const handleIntersection = useCallback(
    (entries) => {
      // There is only one intersectionObserveEntry (entry) - we are only observing one element (the sentinel)
      const [entry] = entries;

      // If sentinel is visible and we have more items and not currently loading
      if (entry.isIntersecting && hasMore && !loading) {
        devLog("Infinite scroll triggered - loading more items");
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, loading],
  );

  /**
   * Effect to detect when DOM elements are ready and trigger observer setup
   * This runs after each render to check if elements have been mounted
   */
  useEffect(() => {
    const sentinelReady = !!sentinelRef.current;
    const rootReady = !rootRef || !!rootRef.current;

    // Only update state if elements just became ready (prevents infinite loop)
    if (sentinelReady && rootReady && !elementsReadyRef.current) {
      elementsReadyRef.current = true;
      setElementsMounted((prev) => prev + 1);
    } else if ((!sentinelReady || !rootReady) && elementsReadyRef.current) {
      // Reset if elements become unavailable (e.g., component unmounts/remounts)
      elementsReadyRef.current = false;
    }
  });

  /**
   * Effect to create and manage the IntersectionObserver
   */
  useEffect(() => {
    const currentSentinel = sentinelRef.current;
    const currentRoot = rootRef?.current || null;

    // Don't create observer if required elements aren't ready
    if (!currentSentinel) {
      return;
    }

    // If rootRef was provided but isn't ready yet, wait
    if (rootRef && !currentRoot) {
      return;
    }

    // Clean up existing observer if any
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer with current root element
    const options = {
      root: currentRoot,
      rootMargin: "100px",
      threshold,
    };

    observerRef.current = new IntersectionObserver(handleIntersection, options);
    observerRef.current.observe(currentSentinel);

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [handleIntersection, threshold, rootRef, elementsMountedCount]);

  return sentinelRef;
}
